import React, { useRef } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';

/**
 * Subtle copper glow + one-time shimmer-sweep behind the footer brand mark.
 *
 *   • Soft-pulse halo: radial copper gradient softly breathes (opacity 0.25 → 0.55 → 0.25)
 *     while the footer is in view. ~6s loop. Heavy blur keeps it diffuse.
 *
 *   • Shimmer sweep: a diagonal light streak glides across the area once, when the
 *     footer first enters the viewport. Fires only ONCE per page life.
 *
 *   • Respects `prefers-reduced-motion` — disables animations and just renders
 *     the static halo at low opacity.
 *
 *   • No heavy filters during scroll: glow is pre-blurred via CSS blur(40px),
 *     mask is `pointer-events: none`, and the parent uses `transform: translateZ(0)`
 *     so the GPU compositor handles it without layout work.
 */
const FooterBrandGlow = ({ children }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, margin: '0px 0px -10% 0px' });
  const reduceMotion = useReducedMotion();

  return (
    <div
      ref={ref}
      className="relative inline-block"
      style={{ transform: 'translateZ(0)' }}
      data-testid="footer-brand-glow"
    >
      {/* Soft pulsing halo — pure CSS animation so it never depends on
          IntersectionObserver (which can be unreliable inside preview iframes). */}
      <div
        aria-hidden
        className="brand-glow-halo pointer-events-none absolute inset-0 -inset-x-8 sm:-inset-x-16 -inset-y-8 sm:-inset-y-12"
        style={{
          background:
            'radial-gradient(ellipse at 50% 50%, rgba(166,110,44,0.6) 0%, rgba(150,100,45,0.28) 48%, rgba(150,100,45,0) 75%)',
          filter: 'blur(26px)',
        }}
        data-testid="footer-brand-glow-halo"
      />

      {/* One-time diagonal shimmer sweep */}
      {!reduceMotion && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-md"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          <motion.div
            className="absolute top-0 h-full"
            style={{
              width: '40%',
              background:
                'linear-gradient(115deg, transparent 0%, rgba(217,136,55,0.0) 30%, rgba(245,210,160,0.35) 50%, rgba(217,136,55,0.0) 70%, transparent 100%)',
              filter: 'blur(6px)',
              mixBlendMode: 'screen',
            }}
            initial={{ left: '-50%' }}
            animate={inView ? { left: '120%' } : { left: '-50%' }}
            transition={{ duration: 2.2, ease: 'easeOut', delay: 0.25 }}
          />
        </motion.div>
      )}

      {/* Actual logo content sits above the effects */}
      <div className="relative z-10">{children}</div>
    </div>
  );
};

export default FooterBrandGlow;
